/**
 * Master View — the dining "Summary" data pivoted like the SSRS R&D report:
 * per service, menu/seat items down the rows and Pullman carriages across the
 * columns (+ an Items Total column), reproducing the paginated report.
 *
 * On top of the raw report it adds reconciliation totals per carriage: Seats
 * (Seq=0, excluding the Premium/Standard Exclusivity seats, which carry no food
 * order) and Starters / Mains / Desserts — which should all match if the
 * ordering is complete.
 *
 * Every figure is clickable → a popup of the bookings behind it (name, booking
 * ref, table, quantity, notes), reconciling to the clicked figure.
 *
 * Data: GET /api/dashboards/bookings/reports?report=master (item × carriage ×
 * service, count = Covers for seats + Courses for dishes). Drill-down: GET
 * /api/dashboards/bookings/cell.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { printSummaryPdf, printDetailPdf, type DetailPrintRow } from "./masterViewPdf";
import { OrientationToggle, type PdfOrientation } from "./orientation";
import { variantLabel, variantParam, type CountVariant, type DedupInfo } from "./bookingsVariant";
import "./masterview.css";

interface MasterRow {
  JourneyDate: string;
  Day: string | null;
  JourneyTime: string | null;
  JourneyCode: string | null;
  Carriage: string | null;
  CarriageSortSeq: number | null;
  Item: string | null;
  Seq: number | null;
  Count: number;
}
interface CellRow {
  Booking: number | null;
  AccName: string | null;
  Seats: string | null;
  Notes: string | null;
  Qty: number;
}

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
function prettyDate(iso: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  if (!m) return iso;
  return `${m[3]} ${MONTHS[Number(m[2]) - 1]} ${m[1]}`;
}
function prettyDateTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString("en-GB", {
    day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit", hour12: false,
  });
}
const num = new Intl.NumberFormat("en-GB");

export interface ServiceNote {
  note: string | null;
  updated_by: string | null;
  updated_at: string | null;
}

/** Shorten a full carriage name to its Pullman name for the column header. */
function carriageLabel(c: string): string {
  return c.replace(/^Pullman\s*-\s*/i, "").trim() || c;
}

type CellQuery = {
  journeyDate: string;
  journeyTime: string;
  carriage?: string;
  item?: string;
  seq?: number;
  excl?: boolean;
  title: string;
};

export interface Service {
  key: string;
  journeyDate: string;
  journeyTime: string;
  day: string;
  journey: string;
  carriages: string[]; // full names, ordered
  items: { item: string; seq: number; byCarriage: Record<string, number>; total: number }[];
}

export function MasterView(props: {
  from: string;
  to: string;
  reloadNonce: number;
  onLastRefresh?: (v: string | null) => void;
  /** Counting variant (As MERAC reports / One count per booking) — rides on every fetch. */
  variant?: CountVariant;
  /** What the server de-duplicated, for the note under the dropdown. */
  onDedup?: (d: DedupInfo | null) => void;
}) {
  const { from, to, reloadNonce, onLastRefresh, onDedup } = props;
  const variant: CountVariant = props.variant ?? "merac";
  const [rows, setRows] = useState<MasterRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [truncated, setTruncated] = useState(false);
  const [bd, setBd] = useState<{ title: string; loading: boolean; error: string | null; rows: CellRow[]; total: number } | null>(null);
  // Seats sold (Pullman occupancy) per service, keyed "YYYY-MM-DD|HH:MM".
  const [occ, setOcc] = useState<Map<string, number>>(new Map());
  // Free-text service notes, same key. Editable by anyone; saved server-side.
  const [notes, setNotes] = useState<Map<string, ServiceNote>>(new Map());

  useEffect(() => {
    let cancelled = false;
    if (from > to) {
      setError("'From' must be on or before 'To'.");
      setRows([]);
      setTruncated(false);
      return;
    }
    setLoading(true);
    setError(null);
    fetch(`/api/dashboards/bookings/reports?report=master&from=${from}&to=${to}&limit=20000${variantParam(variant)}`, { credentials: "include" })
      .then(async (res) => {
        if (!res.ok) {
          const b = await res.json().catch(() => ({}));
          throw new Error(b.error || `Request failed (${res.status})`);
        }
        return res.json() as Promise<{
          rows: MasterRow[];
          last_refresh?: string | null;
          truncated?: boolean;
          dedup?: DedupInfo;
          occupancy?: { service_date: string | null; departure_time: string | null; people_total: number }[];
        }>;
      })
      .then((data) => {
        if (cancelled) return;
        setRows(data.rows ?? []);
        setTruncated(Boolean(data.truncated));
        onLastRefresh?.(data.last_refresh ?? null);
        onDedup?.(data.dedup ?? null);
        const m = new Map<string, number>();
        for (const o of data.occupancy ?? []) {
          if (o.service_date && o.departure_time != null) {
            m.set(`${o.service_date}|${o.departure_time}`, o.people_total);
          }
        }
        setOcc(m);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : String(err));
        setRows([]);
        setTruncated(false);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [from, to, reloadNonce, onLastRefresh, onDedup, variant]);

  // Load saved service notes for the range (non-critical — silent on failure).
  useEffect(() => {
    let cancelled = false;
    if (from > to) return;
    fetch(`/api/dashboards/bookings/service-notes?from=${from}&to=${to}`, { credentials: "include" })
      .then((res) => (res.ok ? (res.json() as Promise<{ notes?: ({ service_date: string | null; departure_time: string | null } & ServiceNote)[] }>) : Promise.reject(new Error(String(res.status)))))
      .then((data) => {
        if (cancelled) return;
        const m = new Map<string, ServiceNote>();
        for (const n of data.notes ?? []) {
          if (n.service_date && n.departure_time != null) {
            m.set(`${n.service_date}|${n.departure_time}`, { note: n.note, updated_by: n.updated_by, updated_at: n.updated_at });
          }
        }
        setNotes(m);
      })
      .catch(() => { /* notes are non-critical */ });
    return () => { cancelled = true; };
  }, [from, to, reloadNonce]);

  const saveNote = useCallback(async (serviceDate: string, departureTime: string, note: string): Promise<void> => {
    const res = await fetch(`/api/dashboards/bookings/service-notes`, {
      method: "PUT",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ service_date: serviceDate, departure_time: departureTime, note }),
    });
    if (!res.ok) {
      const b = await res.json().catch(() => ({}));
      throw new Error((b as { error?: string }).error || `Save failed (${res.status})`);
    }
    const data = (await res.json()) as { note: ServiceNote | null };
    const saved: ServiceNote = data.note ?? { note, updated_by: null, updated_at: null };
    setNotes((prev) => {
      const m = new Map(prev);
      m.set(`${serviceDate}|${departureTime}`, saved);
      return m;
    });
  }, []);

  // Group into services, each with carriage columns and item rows.
  const services = useMemo<Service[]>(() => {
    const map = new Map<string, Service>();
    const carriageSort = new Map<string, number>();
    for (const r of rows) {
      if (!r.Item || !r.Carriage) continue;
      const key = `${r.JourneyDate}|${r.JourneyTime ?? ""}`;
      let svc = map.get(key);
      if (!svc) {
        svc = {
          key,
          journeyDate: r.JourneyDate,
          journeyTime: r.JourneyTime ?? "",
          day: r.Day ?? "",
          journey: r.JourneyCode ?? "",
          carriages: [],
          items: [],
        };
        map.set(key, svc);
      }
      carriageSort.set(r.Carriage, r.CarriageSortSeq ?? 99);
      // carriage set
      if (!svc.carriages.includes(r.Carriage)) svc.carriages.push(r.Carriage);
      // item row
      let it = svc.items.find((x) => x.item === r.Item);
      if (!it) {
        it = { item: r.Item, seq: r.Seq ?? 4, byCarriage: {}, total: 0 };
        svc.items.push(it);
      }
      it.byCarriage[r.Carriage] = (it.byCarriage[r.Carriage] ?? 0) + r.Count;
      it.total += r.Count;
    }
    for (const svc of map.values()) {
      svc.carriages.sort((a, b) => (carriageSort.get(a) ?? 99) - (carriageSort.get(b) ?? 99) || a.localeCompare(b));
      svc.items.sort((a, b) => a.seq - b.seq || a.item.localeCompare(b.item));
    }
    return [...map.values()].sort((a, b) => a.key.localeCompare(b.key));
  }, [rows]);

  const openCell = useCallback((q: CellQuery) => {
    setBd({ title: q.title, loading: true, error: null, rows: [], total: 0 });
    const qs = new URLSearchParams({ journeyDate: q.journeyDate, journeyTime: q.journeyTime });
    if (q.carriage) qs.set("carriage", q.carriage);
    if (q.item) qs.set("item", q.item);
    if (q.seq != null) qs.set("seq", String(q.seq));
    if (q.excl) qs.set("exclExclusivity", "1");
    qs.set("variant", variant);
    fetch(`/api/dashboards/bookings/cell?${qs.toString()}`, { credentials: "include" })
      .then(async (res) => {
        if (!res.ok) {
          const b = await res.json().catch(() => ({}));
          throw new Error(b.error || `Request failed (${res.status})`);
        }
        return res.json() as Promise<{ rows: CellRow[]; total: number }>;
      })
      .then((d) => setBd({ title: q.title, loading: false, error: null, rows: d.rows ?? [], total: d.total ?? 0 }))
      .catch((err) => setBd({ title: q.title, loading: false, error: err instanceof Error ? err.message : String(err), rows: [], total: 0 }));
  }, [variant]);

  const [printing, setPrinting] = useState(false);
  // PDF orientation for the Summary tab's printable outputs (default landscape).
  const [orientation, setOrientation] = useState<PdfOrientation>("landscape");
  // Print the Summary pivot (as shown) with reconciliation + notes per service.
  const onPrintSummary = useCallback(() => {
    printSummaryPdf(services, occ, notes, { from, to }, orientation, variantLabel(variant));
  }, [services, occ, notes, from, to, orientation, variant]);
  // Print the detail — every booking behind each service (fetches the detail
  // report for the same range, groups service -> booking, includes notes).
  const onPrintDetail = useCallback(async () => {
    setPrinting(true);
    try {
      const res = await fetch(`/api/dashboards/bookings/reports?report=detail&from=${from}&to=${to}&limit=20000${variantParam(variant)}`, { credentials: "include" });
      if (!res.ok) throw new Error(`Request failed (${res.status})`);
      const data = (await res.json()) as { rows: DetailPrintRow[] };
      printDetailPdf(data.rows ?? [], notes, { from, to }, orientation, variantLabel(variant));
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setPrinting(false);
    }
  }, [from, to, notes, orientation, variant]);

  if (error) return <div className="bookings-empty bookings-empty--error">Couldn’t load Master View: {error}</div>;
  if (loading && rows.length === 0) return <div className="bookings-empty">Loading…</div>;
  if (services.length === 0) return <div className="bookings-empty">No dining services in this date range.</div>;

  return (
    <div className="mv">
      <div className="mv-toolbar">
        <p className="mv-hint">Every figure is clickable — tap any number to see the bookings behind it.</p>
        <div className="mv-toolbar-actions">
          <OrientationToggle value={orientation} onChange={setOrientation} />
          <button type="button" className="mv-print" onClick={onPrintSummary} disabled={services.length === 0}>
            🖨 Print summary
          </button>
          <button type="button" className="mv-print" onClick={() => void onPrintDetail()} disabled={printing || services.length === 0}>
            {printing ? "Preparing…" : "🖨 Print detail (bookings)"}
          </button>
        </div>
      </div>
      {services.map((svc) => {
        const skey = `${svc.journeyDate}|${svc.journeyTime.slice(11)}`;
        return (
          <ServiceTable
            key={svc.key}
            svc={svc}
            openCell={openCell}
            seatsSold={occ.get(skey)}
            note={notes.get(skey)}
            onSaveNote={(text) => saveNote(svc.journeyDate, svc.journeyTime.slice(11), text)}
          />
        );
      })}
      {truncated && (
        <p className="bookings-truncated">
          Showing the first rows only — totals may be incomplete; narrow the date range to see everything.
        </p>
      )}
      {bd && <CellModal bd={bd} onClose={() => setBd(null)} />}
    </div>
  );
}

function ServiceTable({ svc, openCell, seatsSold, note, onSaveNote }: {
  svc: Service;
  openCell: (q: CellQuery) => void;
  seatsSold?: number;
  note?: ServiceNote;
  onSaveNote: (text: string) => Promise<void>;
}) {
  const cars = svc.carriages;

  // Editable service note. Draft syncs from the loaded note until the user edits.
  const [draft, setDraft] = useState(note?.note ?? "");
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState<string | null>(null);
  useEffect(() => {
    if (!dirty) setDraft(note?.note ?? "");
  }, [note, dirty]);
  const doSaveNote = async () => {
    setSaving(true);
    setSaveMsg(null);
    try {
      await onSaveNote(draft);
      setDirty(false);
      setSaveMsg("Saved");
    } catch (e) {
      setSaveMsg(e instanceof Error ? e.message : "Save failed");
    } finally {
      setSaving(false);
    }
  };
  const svcTitle = `${svc.day} ${prettyDate(svc.journeyDate)} ${svc.journeyTime.slice(11) || ""}`.trim();
  const cell = (n: number) => (n ? num.format(n) : "");

  // Per-carriage reconciliation totals.
  const groupTotals = (predicate: (it: Service["items"][number]) => boolean) => {
    const byCar: Record<string, number> = {};
    let total = 0;
    for (const it of svc.items) {
      if (!predicate(it)) continue;
      for (const c of cars) byCar[c] = (byCar[c] ?? 0) + (it.byCarriage[c] ?? 0);
      total += it.total;
    }
    return { byCar, total };
  };
  const seats = groupTotals((it) => it.seq === 0 && !/Exclusivity/i.test(it.item));
  const exclusivity = groupTotals((it) => it.seq === 0 && /Exclusivity/i.test(it.item));
  const starters = groupTotals((it) => it.seq === 1);
  const mains = groupTotals((it) => it.seq === 2);
  const desserts = groupTotals((it) => it.seq === 3);

  // Reconcile food covers against seats actually sold (Pullman occupancy):
  // seats sold = food covers + exclusivity seats (no meal) + any seat sold with
  // no dining cover recorded.
  const noMeal = seatsSold != null ? seatsSold - seats.total - exclusivity.total : null;

  const totalRow = (label: string, gt: { byCar: Record<string, number>; total: number }, q: Omit<CellQuery, "title" | "journeyDate" | "journeyTime" | "carriage">) => (
    <tr className="mv-totalrow">
      <td className="mv-item">{label}</td>
      {cars.map((c) => (
        <td key={c} className="ta-right mv-clk" onClick={() => openCell({ journeyDate: svc.journeyDate, journeyTime: svc.journeyTime, carriage: c, ...q, title: `${label} · ${carriageLabel(c)} · ${svcTitle}` })}>
          {cell(gt.byCar[c] ?? 0)}
        </td>
      ))}
      <td className="ta-right mv-total mv-clk" onClick={() => openCell({ journeyDate: svc.journeyDate, journeyTime: svc.journeyTime, ...q, title: `${label} · all carriages · ${svcTitle}` })}>
        {cell(gt.total)}
      </td>
    </tr>
  );

  return (
    <section className="mv-service">
      <div className="mv-service-head">
        <h3>{svc.day} {prettyDate(svc.journeyDate)} · {svc.journeyTime.slice(11) || svc.journeyTime}</h3>
        <span className="mv-journey">{svc.journey}</span>
      </div>
      <div className="mv-table-wrap">
        <table className="mv-table">
          <thead>
            <tr>
              <th className="mv-item">Item</th>
              {cars.map((c) => <th key={c} className="ta-right">{carriageLabel(c)}</th>)}
              <th className="ta-right mv-total">Items Total</th>
            </tr>
          </thead>
          <tbody>
            {svc.items.map((it) => (
              <tr key={it.item}>
                <td className="mv-item">{it.item}</td>
                {cars.map((c) => {
                  const v = it.byCarriage[c] ?? 0;
                  return (
                    <td key={c} className={"ta-right" + (v ? " mv-clk" : "")}
                        onClick={v ? () => openCell({ journeyDate: svc.journeyDate, journeyTime: svc.journeyTime, carriage: c, item: it.item, title: `${it.item} · ${carriageLabel(c)} · ${svcTitle}` }) : undefined}>
                      {cell(v)}
                    </td>
                  );
                })}
                <td className="ta-right mv-total mv-clk"
                    onClick={() => openCell({ journeyDate: svc.journeyDate, journeyTime: svc.journeyTime, item: it.item, title: `${it.item} · all carriages · ${svcTitle}` })}>
                  {cell(it.total)}
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            {totalRow("Seats (food)", seats, { seq: 0, excl: true })}
            {totalRow("Starters total", starters, { seq: 1 })}
            {totalRow("Mains total", mains, { seq: 2 })}
            {totalRow("Desserts total", desserts, { seq: 3 })}
          </tfoot>
        </table>
      </div>
      <p className="mv-recon">
        {seatsSold != null ? (
          <>
            Seats sold (Pullman) <strong>{num.format(seatsSold)}</strong> = food covers{" "}
            <strong>{num.format(seats.total)}</strong>
            {exclusivity.total > 0 && (
              <> + exclusivity seats <strong>{num.format(exclusivity.total)}</strong></>
            )}
            {noMeal != null && noMeal !== 0 && (
              <> + no-meal / unmatched <strong>{num.format(noMeal)}</strong></>
            )}
            . Food covers exclude exclusivity seats (bought to privatise a table — no meal).
          </>
        ) : (
          <>
            Food covers <strong>{num.format(seats.total)}</strong>
            {exclusivity.total > 0 && (
              <> · exclusivity seats <strong>{num.format(exclusivity.total)}</strong> (no meal)</>
            )}
          </>
        )}
      </p>
      <div className="mv-notes-box">
        <label className="mv-notes-label" htmlFor={`note-${svc.key}`}>Service notes</label>
        <textarea
          id={`note-${svc.key}`}
          className="mv-notes-input"
          value={draft}
          rows={3}
          placeholder="Add a note for this service (kitchen, allergies, VIPs, changes…). Anyone can edit."
          onChange={(e) => { setDraft(e.target.value); setDirty(true); setSaveMsg(null); }}
        />
        <div className="mv-notes-actions">
          <button type="button" className="mv-notes-save" onClick={() => void doSaveNote()} disabled={saving || !dirty}>
            {saving ? "Saving…" : "Save note"}
          </button>
          {saveMsg && <span className="mv-notes-msg">{saveMsg}</span>}
          {note?.updated_at && !dirty && (
            <span className="mv-notes-meta">
              Last edited{note.updated_by ? ` by ${note.updated_by}` : ""} · {prettyDateTime(note.updated_at)}
            </span>
          )}
        </div>
      </div>
    </section>
  );
}

function CellModal({ bd, onClose }: {
  bd: { title: string; loading: boolean; error: string | null; rows: CellRow[]; total: number };
  onClose: () => void;
}) {
  return (
    <div className="mv-modal-backdrop" role="dialog" aria-modal="true" onClick={onClose}>
      <div className="mv-modal" onClick={(e) => e.stopPropagation()}>
        <div className="mv-modal-head">
          <div>
            <div className="mv-modal-title">{bd.title}</div>
            <div className="mv-modal-sub">{bd.rows.length} booking{bd.rows.length === 1 ? "" : "s"} · total {num.format(bd.total)}</div>
          </div>
          <button type="button" className="mv-modal-close" onClick={onClose} aria-label="Close">×</button>
        </div>
        <div className="mv-modal-body">
          {bd.loading ? (
            <div className="bookings-empty">Loading…</div>
          ) : bd.error ? (
            <div className="bookings-empty bookings-empty--error">{bd.error}</div>
          ) : bd.rows.length === 0 ? (
            <div className="bookings-empty">No bookings for this selection.</div>
          ) : (
            <table className="mv-bd-table">
              <thead>
                <tr><th>Customer</th><th className="ta-right">Qty</th><th>Booking</th><th>Table</th><th>Notes</th></tr>
              </thead>
              <tbody>
                {bd.rows.map((r, i) => (
                  <tr key={`${r.Booking ?? i}`}>
                    <td>{r.AccName ?? "—"}</td>
                    <td className="ta-right">{num.format(r.Qty)}</td>
                    <td>{r.Booking ?? "—"}</td>
                    <td>{r.Seats ?? "—"}</td>
                    <td className="mv-notes">{r.Notes ?? ""}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}
